/* eslint-disable func-names */
import { all, call, put, select } from 'redux-saga/effects';

import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { fetchKnm, fetchKnmDraft, fetchLateChangesFields } from 'APIPackage/KnmModule/services/knmApi';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { setLawInterfaceAction } from 'CorePackage/MainModule/actions/coreActions';
import { NOT_FOUND_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import {
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_LESS_30_DAYS,
  KNM_NOT_FOUND_ERROR,
} from 'CorePackage/MainModule/constants/notices';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { setDirtyActGuid, setDirtyResultDecisionGuid } from 'CorePackage/HeaderModule/actions/headerActions';

import {
  knmAddVisibleModal,
  knmDraftDelete,
  knmGetFailure,
  knmGetRequest,
  knmGetSuccess,
  knmLateChangesFieldsGetFailure,
  knmLateChangesFieldsGetSuccess,
  knmSetAccessNoticeErpId,
  knmSetAccessNoticeKnoOrganizationName,
} from '../actions/knmActions';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { getKnmPath } from '../selectors/knmSelectors';
import { ROUTE_ANALYTICS_KNM_ID, ROUTE_ANALYTICS_PM_ID } from '../../../CorePackage/MainModule/routes/routes';
import { IKnmHistoryItem } from '../../../APIPackage/KnmModule/interfaces/IKnmHistoryItem';
import { getUserRoleIsProsecutorOrSuperProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { AUTOSAVE_DRAFT_VERSION } from '../constants/autosaveDraftVersion';
import { ResponseError } from '../../../APIPackage/MainModule/types/ResponseError';

export function* fetchKnmSaga(knmId: number, onSuccess?: (data: IKnm) => void) {
  yield put(knmGetRequest());
  try {
    const state: IRootState = yield select();
    const path = getKnmPath(state);
    const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
    let knmData: IKnm | null = null;

    // Запускаем оба запроса параллельно
    const {
      draftResult,
      knmResult,
    }: {
      draftResult: { data?: IKnm; error?: IResponseError };
      knmResult: { data?: IKnm; error?: IResponseError };
    } = yield all({
      // Запрос черновика
      draftResult: isErknm
        ? call(function* () {
            try {
              const data: IKnm = yield call(fetchKnmDraft, knmId);

              return { data };
            } catch (error) {
              return { error: error as IResponseError };
            }
          })
        : { data: undefined },

      // Основной запрос КНМ
      knmResult: call(function* () {
        try {
          const data: IKnm = yield call(fetchKnm, knmId);

          return { data };
        } catch (error) {
          return { error: error as IResponseError };
        }
      }),
    });

    if (knmResult.error) {
      throw new ResponseError(knmResult.error);
    }

    // Если черновик получен успешно
    if (draftResult.data) {
      // Проверяем версию черновика
      if (draftResult?.data.draftVersion === AUTOSAVE_DRAFT_VERSION) {
        knmData = { ...(draftResult?.data || {}), draft: true };
      } else {
        // Если версии не совпадают, очищаем черновик и используем основной ресурс если он получен
        yield put(knmDraftDelete(knmId, undefined, false));
        if (knmResult.data) {
          knmData = knmResult.data;
        }
      }
    }
    // Если черновика нет, но основной запрос успешен
    else if (knmResult.data) {
      knmData = knmResult.data;
    }

    if (isErknm && (knmData?.version === KnmVersionsEnum.ERP || knmData?.version === null)) {
      yield put(setLawInterfaceAction(LawInterfacesEnum.FZ_294));
      knmData = yield fetchKnm(knmId);
    } else if (!isErknm && knmData?.version === KnmVersionsEnum.ERKNM) {
      yield put(setLawInterfaceAction(LawInterfacesEnum.FZ_248));
      knmData = yield fetchKnm(knmId);
    }

    const isProsecutorOrSuperProsecutor: boolean | undefined = getUserRoleIsProsecutorOrSuperProsecutor(state);

    if (isProsecutorOrSuperProsecutor && (path === ROUTE_ANALYTICS_KNM_ID || path === ROUTE_ANALYTICS_PM_ID)) {
      try {
        const lateFieldsChangesData: IKnmHistoryItem[] | null = yield fetchLateChangesFields(knmId);

        yield put(knmLateChangesFieldsGetSuccess(lateFieldsChangesData));
      } catch (ex) {
        yield put(knmLateChangesFieldsGetFailure());
      }
    }

    yield put(setDirtyActGuid(undefined));
    yield put(setDirtyResultDecisionGuid(undefined));
    if (knmData) {
      yield put(knmGetSuccess(knmData));
      if (onSuccess) {
        yield onSuccess(knmData);
      }
    }
  } catch (err) {
    const error: IResponseError = err as IResponseError;

    yield put(knmGetFailure());
    const isNotFoundError = error.error === NOT_FOUND_ERROR;

    if (error.status === 403) {
      if (error?.message?.includes('ACCESS_DENIED_FOR_TEST')) {
        yield put(knmAddVisibleModal(KnmModalsEnum.ACCESS_DENIED_FOR_TEST));
      } else {
        yield put(knmSetAccessNoticeErpId(error.erpId));
        yield put(knmSetAccessNoticeKnoOrganizationName(error.knoOrganizationName));
        yield put(knmAddVisibleModal(KnmModalsEnum.ACCESS_RESTRICTION_NOTICE));
      }
    } else if (isNotFoundError) {
      yield put(
        addNoticeAction({
          renderContent: KNM_NOT_FOUND_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_NOT_FOUND,
        }),
      );
    } else if (error?.message?.includes('KNM_NOT_AVAILABLE_LINK_TO_PLAN')) {
      yield put(
        addNoticeAction({
          renderContent: KNM_NOT_AVAILABLE_LINK_TO_PLAN_LESS_30_DAYS,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
        }),
      );
    }
  }
}
